import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Platform,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import FontFamily from '../../../../constants/fonts';

interface OptionItem<T> {
  value: T;
  label: string;
}

interface DropdownSelectProps<T> {
  value: T;
  options: OptionItem<T>[];
  onChange: (val: T) => void;
  placeholder?: string;
  disabled?: boolean;
  isInvalid?: boolean;
  minWidth?: number;
  testID?: string;
}

export function DropdownSelect<T extends string | number>({
  value,
  options,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  isInvalid = false,
  minWidth,
  testID,
}: DropdownSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0, width: 0 });
  const triggerRef = useRef<View>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  const handleOpen = () => {
    if (disabled) return;
    if (triggerRef.current) {
      triggerRef.current.measureInWindow((x, y, width, height) => {
        setCoords({ x, y: y + height + 4, width });
        setIsOpen(true);
      });
    } else {
      setIsOpen(true);
    }
  };

  // The menu lives in a Modal (a portal on web), outside the page's scroll container, so its
  // absolute position is not tied to the trigger. Re-measure while open so it stays anchored
  // when the page scrolls or the window resizes.
  const syncPosition = useCallback(() => {
    triggerRef.current?.measureInWindow((x, y, width, height) => {
      const next = { x, y: y + height + 4, width };
      setCoords((prev) =>
        prev.x === next.x && prev.y === next.y && prev.width === next.width ? prev : next
      );
    });
  }, []);

  useEffect(() => {
    if (!isOpen || Platform.OS !== 'web' || typeof window === 'undefined') return;
    // Scroll events do not bubble; capture catches scrolling of any ancestor container.
    window.addEventListener('scroll', syncPosition, true);
    window.addEventListener('resize', syncPosition);
    return () => {
      window.removeEventListener('scroll', syncPosition, true);
      window.removeEventListener('resize', syncPosition);
    };
  }, [isOpen, syncPosition]);

  return (
    <View ref={triggerRef} style={[styles.wrap, minWidth ? { minWidth } : null]} testID={testID}>
      <TouchableOpacity
        style={[
          styles.triggerBtn,
          isOpen && styles.triggerBtnOpen,
          isInvalid && styles.triggerBtnInvalid,
          disabled && styles.triggerBtnDisabled,
        ]}
        onPress={handleOpen}
        activeOpacity={0.8}
        disabled={disabled}
      >
        <Text
          style={[
            styles.valueText,
            !selectedOption && styles.placeholderText,
            disabled && styles.disabledText,
          ]}
          numberOfLines={1}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </Text>
        <MaterialIcons
          name={isOpen ? 'keyboard-arrow-up' : 'keyboard-arrow-down'}
          size={20}
          color={disabled ? '#94A3B8' : '#64748B'}
        />
      </TouchableOpacity>

      <Modal
        visible={isOpen}
        transparent
        animationType="none"
        onRequestClose={() => setIsOpen(false)}
      >
        <Pressable style={styles.modalBackdrop} onPress={() => setIsOpen(false)}>
          <Pressable
            style={[
              styles.dropdownMenu,
              {
                top: coords.y,
                left: coords.x,
                width: Math.max(coords.width, 160),
              },
            ]}
            onPress={(e) => e.stopPropagation()}
          >
            <ScrollView style={styles.optionsList} nestedScrollEnabled keyboardShouldPersistTaps="handled">
              {options.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <TouchableOpacity
                    key={String(opt.value)}
                    style={[styles.optionItem, isSelected && styles.optionItemSelected]}
                    onPress={() => {
                      onChange(opt.value);
                      setIsOpen(false);
                    }}
                    activeOpacity={0.7}
                  >
                    <Text style={[styles.optionText, isSelected && styles.optionTextSelected]}>
                      {opt.label}
                    </Text>
                    {isSelected && (
                      <MaterialIcons name="check" size={16} color="#034895" />
                    )}
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    width: '100%',
  },
  triggerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#949494',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 4,
    minHeight: 32,
  },
  triggerBtnOpen: {
    borderColor: '#034895',
  },
  triggerBtnInvalid: {
    borderColor: '#EF4444',
    backgroundColor: '#FEF2F2',
  },
  triggerBtnDisabled: {
    backgroundColor: '#F8F8F8',
    borderColor: '#EBEBEB',
  },
  valueText: {
    flex: 1,
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: '#2D2D2D',
    marginRight: 6,
  },
  placeholderText: {
    color: '#94A3B8',
    fontFamily: FontFamily.regular,
  },
  disabledText: {
    color: '#94A3B8',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
  },
  dropdownMenu: {
    position: 'absolute',
    maxHeight: 260,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    ...Platform.select({
      web: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
      },
      default: {
        elevation: 6,
      },
    }),
    overflow: 'hidden',
  },
  optionsList: {
    maxHeight: 250,
  },
  optionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#F1F5F9',
  },
  optionItemSelected: {
    backgroundColor: '#F0F6FD',
  },
  optionText: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: '#2D2D2D',
  },
  optionTextSelected: {
    fontFamily: FontFamily.bold,
    color: '#034895',
  },
});
